import React from 'react';
import { Card, CardContent, TextField, Typography, Switch, FormControl, FormGroup, FormControlLabel, Button, InputLabel, Select, MenuItem, Grid, FormHelperText, LinearProgress } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useQueryParam, StringParam } from 'use-query-params';
import { useSelector } from 'react-redux';
import SimpleReactValidator from 'simple-react-validator';
import { useHttpRequest } from 'src/utils/httpClient';
import ResolveSubscriptionError from 'src/components/ResolveSubscriptionError';
import SubscriptionCreated from 'src/components/SubscriptionCreated';
import { useSnackbar } from 'src/components/Snackbar';

const useStyles = makeStyles((theme) => ({
    center: {
        marginLeft: 'auto',
        marginRight: 'auto'
    },
    header: {
        backgroundColor: '#254a8e'
    },
    logo: {
        maxWidth: '20%',
        minWidth: 100,
        padding: theme.spacing(2)
    },
    cardHeader: {
        marginBottom: theme.spacing(3)
    },
    card: {
        maxWidth: '100%',
        width: '1000px',
        minHeight: '100%',
        marginLeft: 'auto',
        marginRight: 'auto',
        marginBottom: theme.spacing(2),
        flex: 1,
        display: 'flex',
        flexDirection: 'column'
    },
    contentCard: {
        maxWidth: '100%',
        width: '1000px',
        marginLeft: 'auto',
        marginRight: 'auto',
        marginBottom: theme.spacing(2),
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        boxShadow: 'none',
        backgroundColor: '#fafafa'
    },
    input: {
        width: '100%',
        backgroundColor: '#fff',
        margin: '9px 0'
    },
    select: {
        minWidth: '40%'
    },
    button: {
        padding: theme.spacing(2, 8),
        color: '#fff',
        backgroundColor: '#00A300',
        '&:hover': {
            backgroundColor: '#00D100',
            boxShadow: 'none',
        }
    }
}));

const defaultFields = {
    useLongcode: false,
    alphanumericSender: '',
    displayName: '',
    companyName: '',
    streetAddress: '',
    city: '',
    postalCode: '',
    country: '',
    mail: '',
    organizationNumber: '',
    addressType: '',
    name: '',
    department: '',
    surname: '',
    givenName: ''
};

function MarketplaceForm() {
    const classes = useStyles();
    const [marketplaceToken] = useQueryParam('token', StringParam);
    const { userInfo } = useSelector((state) => state.auth);
    const [formData, setFormData] = React.useState({ ...defaultFields });
    const [showValidationMessages, setShowValidationMessages] = React.useState(false);
    const [marketplaceSubscription, setMarketplaceSubscription] = React.useState(null);
    const [subscriptionCreated, setSubscriptionCreated] = React.useState(false);
    const { error: errorSnack } = useSnackbar();

    const { mutate: resolveMarketplaceSubscription, loading: resolveMarketplaceSubscriptionLoading, error: resolveMarketplaceSubscriptionError } = useHttpRequest((token) => ({
        method: 'GET',
        endpoint: '/azuremarketplacesubscription/marketplacetoken',
        headers: {
            'marketplace-token': token
        }
    }));

    const { mutate: createMarketplaceSubscription } = useHttpRequest(({ subscriptionId, body }) => ({
        method: 'POST',
        endpoint: `/azuremarketplacesubscription/${subscriptionId}`,
        body
    }));

    React.useEffect(() => {
        const doResolve = async () => {
            const { payload, error } = await resolveMarketplaceSubscription(marketplaceToken);
            if (!error) {
                setMarketplaceSubscription(payload);
            }
        };
        if (marketplaceToken) {
            doResolve();
        }
    }, [marketplaceToken, resolveMarketplaceSubscription]);

    const validator = React.useRef(new SimpleReactValidator({
        element: (message) => message
    })).current;

    if (showValidationMessages) {
        validator.showMessages();
    } else {
        validator.hideMessages();
    }

    const validationMessages = {
        alphanumericSender: (
            formData.useLongcode ?
                validator.message('alphanumericSender', formData.alphanumericSender, 'min:3|max:11') :
                validator.message('alphanumericSender', formData.alphanumericSender, 'required|min:3|max:11')
        ),
        companyName: validator.message('companyName', formData.companyName, 'required'),
        organizationNumber: validator.message('organizationNumber', formData.organizationNumber, 'required'),
        streetAddress: validator.message('streetAddress', formData.streetAddress, 'required'),
        city: validator.message('city', formData.city, 'required'),
        postalCode: validator.message('postalCode', formData.postalCode, 'required'),
        addressType: validator.message('addressType', formData.addressType, 'required'),
        displayName: validator.message('displayName', formData.displayName, 'required'),
        givenName: validator.message('name', formData.givenName, 'required'),
        surname: validator.message('lastname', formData.surname, 'required'),
        mail: validator.message('e-mail', formData.mail, 'required'),
        department: validator.message('department', formData.department, 'required')
    };

    // Populate userinfo when returned by Graph Api
    React.useEffect(() => {
        setFormData((data) => ({ ...data, ...userInfo }));
    }, [userInfo]);

    React.useEffect(() => {
        if (marketplaceSubscription?.subscription?.saasSubscriptionStatus === 'Subscribed') {
            window.location.href = 'https://app.smartdialog.ai';
        }
    }, [marketplaceSubscription]);

    const handleInputChanged = ({ target: { name, value } }) => {
        setFormData({
            ...formData,
            [name]: value
        });
    };

    const handleConfirmClick = async () => {
        setShowValidationMessages(true);

        if (validator.allValid()) {
            const request = {
                name: formData.companyName,
                organizationNumber: formData.organizationNumber,
                countryId: 1,
                address: {
                    type: formData.addressType,
                    address: formData.streetAddress,
                    city: formData.city,
                    zipCode: formData.postalCode
                },
                contactInformation: {
                    type: formData.department,
                    name: formData.displayName,
                    email: formData.mail,
                    displayName: formData.displayName,
                    surname: formData.surname,
                    givenName: formData.givenName
                },
                useLongcode: formData.useLongcode,
                sender: formData.alphanumericSender
            };
            const { error, errorMessages } = await createMarketplaceSubscription({ subscriptionId: marketplaceSubscription?.id, body: request });
            if (!error) {
                setSubscriptionCreated(true);
            } else {
                errorSnack(errorMessages);
            }
        }
    };

    const subscriptionResolved = marketplaceToken && !resolveMarketplaceSubscriptionLoading && !resolveMarketplaceSubscriptionError;
    const error = !resolveMarketplaceSubscriptionLoading && (!marketplaceToken || resolveMarketplaceSubscriptionError || marketplaceSubscription?.subscription?.saasSubscriptionStatus !== 'PendingFulfillmentStart');

    return (
        <Card className={classes.card}>
            <CardContent className={classes.header}>
                <img
                    src="/images/logos/smartdialog_logo_vertical_white.png"
                    alt="Arena Interactive logo"
                    className={classes.logo}
                />
            </CardContent>

            {resolveMarketplaceSubscriptionLoading && <LinearProgress />}
            {error && <ResolveSubscriptionError />}
            {!error && subscriptionCreated && <SubscriptionCreated />}
            {!error && subscriptionResolved && !subscriptionCreated && (
                <>
                    <CardContent>
                        <Card
                            className={classes.contentCard}
                            variant="outlined"
                        >
                            <CardContent>
                                <Typography variant="h6" className={classes.cardHeader}>
                                    Plan information
                                </Typography>
                                <Grid container spacing={2}>
                                    <Grid item md={6} xs={12}>
                                        <Typography variant="body1">
                                            Purchase Id:
                                        </Typography>
                                    </Grid>
                                    <Grid item md={6} xs={12}>
                                        <Typography variant="body1">
                                            {marketplaceSubscription?.id ?? ''}
                                        </Typography>
                                    </Grid>
                                    <Grid item md={6} xs={12}>
                                        <Typography variant="body1">
                                            Offer Id:
                                        </Typography>
                                    </Grid>
                                    <Grid item md={6} xs={12}>
                                        <Typography variant="body1">
                                            {marketplaceSubscription?.offerId ?? ''}
                                        </Typography>
                                    </Grid>
                                    <Grid item md={6} xs={12}>
                                        <Typography variant="body1">
                                            Plan Id:
                                        </Typography>
                                    </Grid>
                                    <Grid item md={6} xs={12}>
                                        <Typography variant="body1">
                                            {marketplaceSubscription?.planId ?? ''}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12}>
                                        <FormControl component="fieldset" variant="outlined" size="small">
                                            <FormGroup>
                                                <FormControlLabel
                                                    control={(
                                                        <Switch
                                                            checked={formData.useLongcode}
                                                            onChange={({ target: { checked } }) => setFormData({ ...formData, useLongcode: checked })}
                                                            color="primary"
                                                        />
                                                    )}
                                                    label="Use longcode"
                                                />
                                            </FormGroup>
                                        </FormControl>
                                        <FormHelperText>
                                            This offer includes the use of a virtual phonenumber, check this box if you want us to get you one. It can be used to receive sms-messages.
                                            <br />
                                            If you do not need one at this time, leave the box unchecked, as it will speed up the setup process.
                                        </FormHelperText>
                                    </Grid>
                                    <Grid item sm={6} xs={12}>
                                        <TextField
                                            className={classes.input}
                                            label="alphanumeric sender (3-11 characters)"
                                            variant="outlined"
                                            name="alphanumericSender"
                                            value={formData.alphanumericSender}
                                            onChange={handleInputChanged}
                                            error={Boolean(validationMessages?.alphanumericSender)}
                                            helperText={validationMessages?.alphanumericSender ?? null}
                                        />
                                        <FormHelperText>
                                            In order to send messages, you need to provide us with the sender you wish to use, if you are using a longcode (above) then you can skip this.
                                        </FormHelperText>
                                    </Grid>
                                </Grid>
                            </CardContent>
                        </Card>
                    </CardContent>
                    <CardContent>
                        <Card
                            className={classes.contentCard}
                            variant="outlined"
                            label="Personal information"
                        >
                            <CardContent>
                                <Typography variant="h6" className={classes.cardHeader}>
                                    Company information
                                </Typography>
                                <Grid container spacing={2}>
                                    <Grid item sm={6} xs={12}>
                                        <TextField
                                            className={classes.input}
                                            label="company name"
                                            variant="outlined"
                                            name="companyName"
                                            value={formData.companyName}
                                            onChange={handleInputChanged}
                                            error={Boolean(validationMessages?.companyName)}
                                            helperText={validationMessages?.companyName ?? null}
                                        />
                                    </Grid>
                                    <Grid item sm={6} xs={12}>
                                        <TextField
                                            className={classes.input}
                                            label="organization number"
                                            variant="outlined"
                                            name="organizationNumber"
                                            value={formData.organizationNumber}
                                            onChange={handleInputChanged}
                                            error={Boolean(validationMessages?.organizationNumber)}
                                            helperText={validationMessages?.organizationNumber ?? null}
                                        />
                                    </Grid>
                                    <Grid item sm={6} xs={12}>
                                        <TextField
                                            className={classes.input}
                                            label="address"
                                            variant="outlined"
                                            name="streetAddress"
                                            value={formData.streetAddress}
                                            onChange={handleInputChanged}
                                            error={Boolean(validationMessages?.streetAddress)}
                                            helperText={validationMessages?.streetAddress ?? null}
                                        />
                                    </Grid>
                                    <Grid item sm={6} xs={12}>
                                        <TextField
                                            className={classes.input}
                                            label="city"
                                            variant="outlined"
                                            name="city"
                                            value={formData.city}
                                            onChange={handleInputChanged}
                                            error={Boolean(validationMessages?.city)}
                                            helperText={validationMessages?.city ?? null}
                                        />
                                    </Grid>
                                    <Grid item sm={6} xs={12}>
                                        <TextField
                                            className={classes.input}
                                            label="Postal Code"
                                            variant="outlined"
                                            name="postalCode"
                                            value={formData.postalCode}
                                            onChange={handleInputChanged}
                                            error={Boolean(validationMessages?.postalCode)}
                                            helperText={validationMessages?.postalCode ?? null}
                                        />
                                    </Grid>
                                    <Grid item sm={6} xs={12}>
                                        <FormControl className={classes.input} variant="outlined" error={Boolean(validationMessages?.addressType)}>
                                            <InputLabel id="addressTypeLabel">address type</InputLabel>
                                            <Select
                                                labelId="addressType"
                                                name="addressType"
                                                label="address type"
                                                onChange={handleInputChanged}
                                                value={formData.addressType}
                                                displayEmpty
                                                error={Boolean(validationMessages?.addressType)}
                                            >
                                                <MenuItem value="HQ">HQ</MenuItem>
                                                <MenuItem value="Billing">Billing</MenuItem>
                                                <MenuItem value="Technical">Technical</MenuItem>
                                                <MenuItem value="Office">Office</MenuItem>
                                            </Select>
                                            {validationMessages?.addressType && <FormHelperText>{validationMessages?.addressType}</FormHelperText>}
                                        </FormControl>
                                    </Grid>
                                </Grid>
                            </CardContent>
                        </Card>
                    </CardContent>
                    <CardContent>
                        <Card
                            className={classes.contentCard}
                            variant="outlined"
                            label="Personal information"
                        >
                            <CardContent>
                                <Typography variant="h6" className={classes.cardHeader}>
                                    Contact person
                                </Typography>
                                <Grid container spacing={2}>
                                    <Grid item sm={6} xs={12}>
                                        <TextField
                                            className={classes.input}
                                            label="displayname"
                                            variant="outlined"
                                            name="displayName"
                                            value={formData.displayName}
                                            onChange={handleInputChanged}
                                            error={Boolean(validationMessages?.displayName)}
                                            helperText={validationMessages?.displayName ?? null}
                                        />
                                    </Grid>
                                    <Grid item sm={6} xs={12}>
                                        <TextField
                                            className={classes.input}
                                            label="e-mail"
                                            variant="outlined"
                                            name="mail"
                                            value={formData.mail}
                                            onChange={handleInputChanged}
                                            error={Boolean(validationMessages?.mail)}
                                            helperText={validationMessages?.mail ?? null}
                                        />
                                    </Grid>
                                    <Grid item sm={6} xs={12}>
                                        <TextField
                                            className={classes.input}
                                            label="name"
                                            variant="outlined"
                                            name="givenName"
                                            value={formData.givenName}
                                            onChange={handleInputChanged}
                                            error={Boolean(validationMessages?.givenName)}
                                            helperText={validationMessages?.givenName ?? null}
                                        />
                                    </Grid>
                                    <Grid item sm={6} xs={12}>
                                        <TextField
                                            className={classes.input}
                                            label="lastname"
                                            variant="outlined"
                                            name="surname"
                                            value={formData.surname}
                                            onChange={handleInputChanged}
                                            error={Boolean(validationMessages?.surname)}
                                            helperText={validationMessages?.surname ?? null}
                                        />
                                    </Grid>
                                    <Grid item sm={6} xs={12}>
                                        <FormControl className={classes.input} variant="outlined" error={Boolean(validationMessages?.department)}>
                                            <InputLabel id="contactTypeLabel">department</InputLabel>
                                            <Select
                                                labelId="contactType"
                                                id="contactType"
                                                label="department"
                                                name="department"
                                                onChange={handleInputChanged}
                                                value={formData.department}
                                                displayEmpty
                                                error={Boolean(validationMessages?.department)}
                                            >
                                                <MenuItem value="Technical">Technical</MenuItem>
                                                <MenuItem value="Finance">Finance</MenuItem>
                                                <MenuItem value="Owner">Owner</MenuItem>
                                                <MenuItem value="Support">Support</MenuItem>
                                            </Select>
                                            {validationMessages?.department && <FormHelperText>{validationMessages?.department}</FormHelperText>}
                                        </FormControl>
                                    </Grid>
                                </Grid>
                            </CardContent>
                        </Card>
                    </CardContent>
                    <CardContent className={classes.center}>
                        <Button
                            variant="outlined"
                            size="large"
                            className={classes.button}
                            onClick={handleConfirmClick}
                            disabled={showValidationMessages && !validator.allValid()}
                        >
                            Confirm
                        </Button>
                    </CardContent>
                </>
            )}
        </Card>
    );
}

export default MarketplaceForm;